import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Home } from 'lucide-react';
import { PublicSection, Reveal } from '@/components/public';
import { getAdjacentSpokes } from '@/config/verticals';

/**
 * SpokeNav — bottom-of-page navigation between spoke pages.
 *
 * Layout:
 *   [ ← Prev spoke (or Homepage if this is spoke #1) ]    [ Next spoke → (or Homepage if this is the last spoke) ]
 *
 * If the adjacent spoke has `built: false`, we render a dimmed
 * "Coming soon: {name}" state instead of a live link.
 */
export default function SpokeNav({ currentSlug }) {
  const { prev, next } = getAdjacentSpokes(currentSlug);

  return (
    <PublicSection variant="muted" testId="spoke-nav-section" className="!py-10 md:!py-14">
      <Reveal>
        <div className="grid gap-4 sm:grid-cols-2" data-testid="spoke-nav">
          {/* Prev slot */}
          {prev ? (
            <NavCard
              to={prev.path}
              built={prev.built}
              direction="prev"
              eyebrow="Previous solution"
              name={prev.name}
              testId={`spoke-nav-prev-${prev.slug}`}
            />
          ) : (
            <NavCard
              to="/"
              built
              direction="home"
              eyebrow="Back to"
              name="Homepage"
              testId="spoke-nav-home-left"
            />
          )}

          {/* Next slot */}
          {next ? (
            <NavCard
              to={next.path}
              built={next.built}
              direction="next"
              eyebrow="Next solution"
              name={next.name}
              testId={`spoke-nav-next-${next.slug}`}
            />
          ) : (
            <NavCard
              to="/"
              built
              direction="home"
              eyebrow="Back to"
              name="Homepage"
              testId="spoke-nav-home-right"
            />
          )}
        </div>
      </Reveal>
    </PublicSection>
  );
}

function NavCard({ to, built, direction, eyebrow, name, testId }) {
  const isNext = direction === 'next';
  const alignRight = isNext;

  const Icon = direction === 'home' ? Home : isNext ? ArrowRight : ArrowLeft;

  const body = (
    <>
      <div className={`flex items-center gap-2 ${alignRight ? 'justify-end' : ''}`}>
        {!alignRight && <Icon className="w-4 h-4 text-primary" strokeWidth={2.5} />}
        <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-primary">
          {eyebrow}
        </p>
        {alignRight && <Icon className="w-4 h-4 text-primary" strokeWidth={2.5} />}
      </div>
      <p
        className={`mt-2 text-lg md:text-xl font-heading font-bold text-foreground ${alignRight ? 'text-right' : ''}`}
      >
        {name}
      </p>
      {!built && (
        <p className={`mt-1 text-xs text-muted-foreground ${alignRight ? 'text-right' : ''}`}>
          Coming soon
        </p>
      )}
    </>
  );

  if (!built) {
    return (
      <div
        data-testid={`${testId}-disabled`}
        aria-disabled="true"
        className="block border border-border bg-background p-5 md:p-6 opacity-60 cursor-not-allowed"
      >
        {body}
      </div>
    );
  }

  return (
    <Link
      to={to}
      data-testid={testId}
      className="block border border-border bg-background p-5 md:p-6 hover:border-primary hover:-translate-y-0.5 transition-[transform,border-color] duration-200"
    >
      {body}
    </Link>
  );
}
